import React, { useState, useEffect } from 'react';
import './RemocaoExames.css';
import { IoTrash } from 'react-icons/io5';
import { sendMessage } from '../../functions/apiCalls/api';
import { ReactComponent as IconHome } from '../../images/icon-service-home-primary-color.svg';
import { ReactComponent as IconUnit } from '../../images/icon-service-unit-primary-color.svg';

const RemocaoExames = ({ 
    message, 
    handleButtonSetMessage, 
    client_id, 
    marca = 'AM', 
    origem = 'chat-web', 
    messages = [], 
    setMessages, 
    localizacao = null, 
    operational_system = 'web', 
    version_app = '1.0.0',
    setRequestId,
    requestImage
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [visible, setVisible] = useState(false);
    const [allExams, setAllExams] = useState([]);
    const [activeExamIds, setActiveExamIds] = useState(new Set());
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [initialMessageCount, setInitialMessageCount] = useState(0);
    const [submitTimeoutId, setSubmitTimeoutId] = useState(null);

    // Função para formatar valor monetário no padrão brasileiro
    const formatCurrency = (value) => {
        const numericValue = typeof value === 'string' ? parseFloat(value) : value;
        
        if (isNaN(numericValue)) return 'R$ 0,00';
        
        return numericValue.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    // Inicializar exames selecionados com os dados da API
    useEffect(() => {
        if (message?.examesSelecionados?.procedimentos) {
            // Converter formato da API para formato do componente
            const examesConvertidos = message.examesSelecionados.procedimentos.map(exam => ({
                id: exam.id,
                name: exam.nome,
                price: exam.valor,
                isMobileCoverage: true // Assumir que tem cobertura móvel por padrão
            }));
            setAllExams(examesConvertidos);
            
            // Inicialmente todos os exames estão ativos
            const allIds = new Set(examesConvertidos.map(exam => exam.id));
            setActiveExamIds(allIds);
        }
    }, [message]);

    // Abrir modal automaticamente quando o componente for montado
    useEffect(() => {
        if (message?.examesSelecionados?.procedimentos && message.examesSelecionados.procedimentos.length > 0) {
            // Pequeno delay para garantir que o componente foi totalmente renderizado
            const timeoutId = setTimeout(() => {
                openModal();
            }, 100);

            return () => clearTimeout(timeoutId);
        }
    }, [message?.examesSelecionados?.procedimentos]);

    // Monitorar mudanças no array de mensagens para detectar resposta da API
    useEffect(() => {
        if (isSubmitting && messages && messages.length > initialMessageCount) {
            // Nova mensagem foi adicionada, verificar se é uma resposta do bot
            const lastMessage = messages[messages.length - 1];
            if (lastMessage && lastMessage.origin === 'bot' && !lastMessage.typing) {
                // Resposta do bot recebida, limpar timeout e parar o loading
                if (submitTimeoutId) {
                    clearTimeout(submitTimeoutId);
                    setSubmitTimeoutId(null);
                }
                setIsSubmitting(false);
                closeModal();
            }
        }
    }, [messages, isSubmitting, initialMessageCount, submitTimeoutId]);

    // Controlar a animação do modal quando é aberto
    useEffect(() => {
        if (isModalOpen) {
            // Inicia a animação após um pequeno delay
            setTimeout(() => {
                setVisible(true);
            }, 10);
            
            // Controlar scroll do body
            document.body.classList.add('modal-open');
            document.body.style.overflow = 'hidden';
        } else {
            setVisible(false);
            document.body.classList.remove('modal-open');
            document.body.style.overflow = 'unset';
        }

        // Cleanup quando componente é desmontado
        return () => {
            document.body.classList.remove('modal-open');
            document.body.style.overflow = 'unset';
        };
    }, [isModalOpen]);

    // Cleanup do timeout quando componente é desmontado
    useEffect(() => {
        return () => {
            if (submitTimeoutId) {
                clearTimeout(submitTimeoutId);
            }
        };
    }, [submitTimeoutId]);

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setVisible(false);
        // Limpar timeout se existir
        if (submitTimeoutId) {
            clearTimeout(submitTimeoutId);
            setSubmitTimeoutId(null);
        }
        // Aguarda a animação antes de fechar completamente
        setTimeout(() => {
            setIsModalOpen(false);
            setIsSubmitting(false);
            setInitialMessageCount(0);
        }, 300);
    };

    const handleUpdateExams = () => {
        // Filtrar apenas exames ativos
        const activeExams = allExams.filter(exam => activeExamIds.has(exam.id));
        
        // Permitir enviar mesmo lista vazia
        if (activeExams.length >= 0) {
            // Iniciar loading
            setIsSubmitting(true);
            setInitialMessageCount(messages ? messages.length : 0);

            // Timeout de segurança caso a API não responda
            const timeoutId = setTimeout(() => {
                console.warn('Timeout ao aguardar resposta da API');
                setIsSubmitting(false);
                closeModal();
            }, 30000); // 30 segundos
            setSubmitTimeoutId(timeoutId);

            // Montar o objeto de exames selecionados conforme especificado
            const examesSelecionados = {
                procedimentos: activeExams.map(exam => ({
                    id: exam.id,
                    nome: exam.name,
                    valor: parseFloat(exam.price) || 0,
                    hasMobileCoverage: exam.hasMobileCoverage || false,
                    hasUnitCoverage: exam.hasUnitCoverage || false
                })),
                valorTotal: activeExams.reduce((total, exam) => {
                    return total + (parseFloat(exam.price) || 0);
                }, 0)
            };

            console.log('Exames atualizados:', examesSelecionados);

            // Fazer chamada da API com os dados no contexto
            if (client_id && setMessages) {
                sendMessage(
                    setMessages,
                    client_id,
                    '', // Mensagem vazia
                    messages,
                    marca,
                    origem,
                    null, // teste
                    localizacao,
                    null, // latitude
                    null, // longitude
                    null, // lastInput
                    null, // setTeste
                    null, // getLocation
                    operational_system,
                    version_app,
                    examesSelecionados, // Passar os exames selecionados como parâmetro adicional
                    null, // additionalContext
                    requestImage, // requestImage
                    setRequestId // setRequestId
                );
            } else {
                console.warn('Parâmetros insuficientes para fazer chamada da API:', { 
                    client_id, 
                    setMessages: !!setMessages 
                });
                // Se não conseguir enviar, limpar timeout e parar o loading
                if (submitTimeoutId) {
                    clearTimeout(submitTimeoutId);
                    setSubmitTimeoutId(null);
                }
                setIsSubmitting(false);
                closeModal();
            }

            // Modal será fechado automaticamente quando a resposta da API chegar
        }
    };

    const handleExamToggle = (examId) => {
        setActiveExamIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(examId)) {
                newSet.delete(examId);
            } else {
                newSet.add(examId);
            }
            return newSet;
        });
    };

    const activeExams = allExams.filter(exam => activeExamIds.has(exam.id));
    const totalValue = activeExams.reduce((total, exam) => {
        return total + (parseFloat(exam.price) || 0);
    }, 0);

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleUpdateExams();
        }
        if (e.key === 'Escape') {
            closeModal();
        }
    };

    return (
        <>
            {/* Componente principal - aparece no chat apenas quando modal não está aberto */}
            {!isModalOpen && (
                <div className="remocao-exames-container">
                    <button 
                        className="remocao-exames-button"
                        onClick={openModal}
                        aria-label="Abrir remoção de exames"
                    >
                        <div className="remocao-content">
                            <IoTrash className="remove-icon" />
                            <span>Gerenciar exames selecionados ({activeExams.length})</span>
                        </div>
                    </button>
                </div>
            )}

            {/* Modal de remoção - surge da base como CSAT */}
            {isModalOpen && (
                <>
                    <div 
                        className={`modal-overlay ${visible ? 'visible' : ''}`} 
                        onClick={closeModal}
                    ></div>
                    <div className={`modal-content-csat ${visible ? 'visible' : ''}`}>
                        <div className="modal-header">
                            <h3>Gerenciar Exames</h3>
                            <button 
                                className="close-button" 
                                onClick={closeModal}
                                aria-label="Fechar modal"
                            >
                                ×
                            </button>
                        </div>
                        
                        <div className="modal-body" onKeyDown={handleKeyPress}>
                            {/* Exames selecionados */}
                            {allExams.length > 0 ? (
                                <div className="selected-exams">
                                    <h4>Exames na lista ({allExams.length}):</h4>
                                    <p className="exam-instruction">Desmarque os exames que deseja remover da lista:</p>
                                    <div className="exam-list">
                                        {allExams.map((exam) => {
                                            const isActive = activeExamIds.has(exam.id);
                                            return (
                                                <div key={exam.id} className={`exam-item ${!isActive ? 'exam-item-inactive' : ''}`}>
                                                    <label className={`exam-checkbox-label ${!isActive ? 'exam-label-inactive' : ''}`}>
                                                        <input
                                                            type="checkbox"
                                                            checked={isActive}
                                                            onChange={() => handleExamToggle(exam.id)}
                                                            className="exam-checkbox"
                                                        />
                                                        <div className="exam-info">
                                                            <div className="exam-details">
                                                                <span className="exam-name">{exam.name}</span>
                                                                <div className="exam-coverage">
                                                                    {exam.isMobileCoverage && (
                                                                        <div className="coverage-item" title="Atendimento domiciliar disponível">
                                                                            <IconHome className="coverage-icon" />
                                                                            <span className="coverage-text">Em casa</span>
                                                                        </div>
                                                                    )}
                                                                    <div className="coverage-item" title="Atendimento em unidades disponível">
                                                                        <IconUnit className="coverage-icon" />
                                                                        <span className="coverage-text">Na unidade</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <span className="exam-price">{formatCurrency(exam.price)}</span>
                                                        </div>
                                                    </label>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    
                                    {/* Total */}
                                    <div className="total-value">
                                        <span className="total-label">Total:</span>
                                        <span className="total-price">{formatCurrency(totalValue)}</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="no-exams">
                                    <p>Nenhum exame selecionado.</p>
                                </div>
                            )}
                        </div>
                        
                        <div className="modal-footer">
                            <button 
                                className={`update-exams-button ${isSubmitting ? 'loading' : ''}`}
                                onClick={handleUpdateExams}
                                disabled={isSubmitting}
                                aria-label="Atualizar lista de exames"
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="loading-spinner"></div>
                                        <span>Atualizando...</span>
                                    </>
                                ) : (
                                    activeExams.length > 0 
                                        ? `Continuar com ${activeExams.length} exame${activeExams.length !== 1 ? 's' : ''}`
                                        : 'Continuar sem exames'
                                )}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default RemocaoExames; 